// ArrPieChart.tsx
'use client';

import React from 'react';
import {PieChart} from '@mui/x-charts/PieChart';
import {Box, Typography} from '@mui/material';
import './ArrPieChart.scss';
import {t} from 'i18next';

export type ArrPieChartData = {
  id: string;
  value: number;
  label: string;
  color: string;
};

export interface ArrPieChartProps {
  /** Chart title (optional) */
  title?: string;
  /** Data items */
  data: ArrPieChartData[];
  /** Total items */
  total: number;
  /** Inner radius of donut */
  innerRadius?: number;
  /** Outer radius of donut */
  outerRadius?: number;
  /** Empty state color */
  emptyColor?: string;
  /** Width of chart */
  width?: number;
  /** Height of chart */
  height?: number;
}

const ArrPieChart: React.FC<ArrPieChartProps> = ({
  title,
  data,
  total = 0,
  innerRadius = 35,
  outerRadius = 65,
  emptyColor = '#344660',
  width = 140,
  height = 140,
}) => {
  const pieChart =
    data.length > 0
      ? data
      : [
          {id: 'completed', value: 0, label: 'Completed', color: '#7DDFBB'},
          {id: 'todo', value: 0, label: 'To Do', color: '#FFC37D'},
        ];
  const hasData = pieChart.some((itm) => itm.value > 0);

  return (
    <Box className="arr-pie-chart-container" data-testid="arr-pie-chart">
      <Typography className="title-text" data-testid="chart-title">
        {title}
        <span className="total-text" data-testid="chart-total">
          {total}
          {t('common.charts.docs')}
        </span>
      </Typography>
      <Box className="chart-container">
        <Box className="pie-chart" data-testid="pie-chart">
          <PieChart
            series={
              hasData
                ? [
                    {
                      innerRadius,
                      outerRadius,
                      data: pieChart,
                      startAngle: 360,
                      endAngle: 0,
                    },
                  ]
                : [
                    {
                      innerRadius,
                      outerRadius,
                      data: [
                        {
                          id: 'empty',
                          value: 1,
                          label: 'Empty',
                          color: emptyColor,
                        },
                      ],
                    },
                  ]
            }
            hideLegend
            width={width}
            height={height}
            slots={{tooltip: () => null}}
          />
        </Box>

        <Box className="legend-container" data-testid="legend-container">
          {pieChart.map((item) => (
            <Box
              key={item.id}
              className="legend-item"
              data-testid="legend-item"
            >
              <Box
                className="legend-color-box"
                sx={{backgroundColor: item.color}}
                data-testid={`legend-color-${item.id}`}
              />
              <Typography
                className="legend-text"
                data-testid={`arr-pie-chart-label`}
              >
                {item.label}
                <br data-testid={`arr-pie-chart-br`} />
                <span
                  className="legend-doc-text"
                  data-testid={`legend-doc-${item.id}`}
                >
                  {item.value} {t('common.charts.docs')}
                </span>
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ArrPieChart;
